import { HubConnectionState } from '@microsoft/signalr';
import {
  ComponentProps,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { BulkEditStatus } from 'src/components/common/BulkActions/BulkEditStatus';
import { createSquareMarketplaceLogos } from 'src/components/common/MarketplaceLogo';
import { useActivePosEntityContext } from 'src/contexts/ActivePosEntityContext';
import { useAppContext } from 'src/contexts/AppContext';
import { useBulkEditHubContext } from 'src/contexts/BulkEditHubContext';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { Content } from 'src/contexts/ContentContext';
import { useLocalizationContext } from 'src/contexts/LocalizationContext';
import { useMultiSelectionContext } from 'src/contexts/MultiSelectionContext';
import { WarningMessage } from 'src/core/POS/MessageWithIcon';
import { vars } from 'src/core/themes';
import { Stack } from 'src/core/ui';
import { AlertWithSuppressionDialog } from 'src/dialogs/AlertWithSuppressionDialog';
import { BulkEditFooter } from 'src/dialogs/BulkEdits/common/BulkEditFooter';
import { useBulkEditSelectionOffices } from 'src/hooks/api/useBulkEditSelectionOffices';
import { useBulkEditHub } from 'src/hooks/useBulkEditHub';
import { usePriceChangeDialogs } from 'src/hooks/usePriceChangeDialogs';
import { useUserHasAnyOfPermissions } from 'src/hooks/useUserHasAnyOfPermissions';
import { DetailGroup } from 'src/modals/common';
import { ContentId } from 'src/utils/constants/contentId';
import {
  getListingDetailsUpdateInput,
  isListingDetailsInputChanged,
} from 'src/utils/inventoryUtils';
import { roundToPrecision } from 'src/utils/numberFormatter';
import {
  ActionOutboxEntityType,
  BulkActionType,
  BulkEditPreviewWithDetails,
  BulkEditProgress,
  BulkEditStep,
  Event,
  ListingDetails,
  ListingDetailsUpdateInput,
  ListingQuery,
  Marketplace,
  MarketplaceAccountSetting,
  Permission,
} from 'src/WebApiController';

import { BulkMarketplacePriceFormInput } from './BulkChangeMarketplacePriceAction.types';
import {
  MarketplacePriceInputHeader,
  MarketplacePriceInputTile,
  OfficeStubHubManualPrices,
  StubHubManualPrice,
} from './BulkEditMarketplacePriceMobileContent.components';
import {
  buildOfficePriceFormDefaults,
  getOfficePriceMode,
  getStubHubSellerFee,
  hasOfficePriceValue,
  isMarketplacePriceOverrideDisabled,
  OfficePriceRow,
} from './officePriceRows.utils';

export type BulkEditMarketplacePricesMobileContentProps = {
  updateKey: string;
  event?: Event;
  filterQuery?: ListingQuery;
  onOkay: (
    listing: BulkMarketplacePriceFormInput | null,
    supportBackgroundProcess?: boolean,
    onPreviewReceived?: (preview: BulkEditPreviewWithDetails) => void,
    preview?: BulkEditPreviewWithDetails
  ) => void;
  onCancel: () => void;
};

const getBulkMarketplacePriceDefaultValues = (
  marketplaceSettings: MarketplaceAccountSetting[] | undefined,
  officePriceRows: OfficePriceRow[]
): BulkMarketplacePriceFormInput => ({
  ...getListingDetailsUpdateInput(null, null, marketplaceSettings),
  // Multi-office selection: one (blank) price row per office; null otherwise
  officePriceUpdates: buildOfficePriceFormDefaults(officePriceRows),
});

export const BulkEditMarketplacePricesMobileContent = ({
  ...rest
}: BulkEditMarketplacePricesMobileContentProps) => {
  const { loginContext } = useAppContext();
  const marketplaceSettings =
    loginContext?.user?.activeAccount?.marketplaceSettings;
  const {
    offices,
    isLoading: isOfficesLoading,
    hasSelectionError,
  } = useBulkEditSelectionOffices(rest.filterQuery);
  const getDefaultValues = useCallback(
    () => getBulkMarketplacePriceDefaultValues(marketplaceSettings, offices),
    [marketplaceSettings, offices]
  );
  const methods = useForm<BulkMarketplacePriceFormInput>({
    defaultValues: getDefaultValues(),
  });

  const { reset } = methods;
  const officeIdsKey = offices
    .map((o) => `${o.officeId}:${o.isDomestic}`)
    .join(',');
  useEffect(() => {
    // Office rows arrive asynchronously - rebuild the form defaults once the
    // selection's fee rows are known (keyed on ids and buckets so name lookups
    // don't wipe input)
    reset(getDefaultValues());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [officeIdsKey]);

  return (
    <FormProvider {...methods}>
      <BulkEditMarketplacePricesMobileContentDetails
        {...rest}
        {...methods}
        officePriceRows={offices}
        isOfficesLoading={isOfficesLoading}
        hasSelectionError={hasSelectionError}
        getDefaultValues={getDefaultValues}
      />
    </FormProvider>
  );
};

const BulkEditMarketplacePricesMobileContentDetails = ({
  officePriceRows,
  isOfficesLoading,
  hasSelectionError,
  getDefaultValues,
  updateKey,
  event,
  onOkay,
  onCancel,
  handleSubmit,
  watch,
  reset,
  formState,
}: BulkEditMarketplacePricesMobileContentProps & {
  officePriceRows: OfficePriceRow[];
  isOfficesLoading: boolean;
  hasSelectionError: boolean;
  getDefaultValues: () => BulkMarketplacePriceFormInput;
} & Omit<
    ComponentProps<typeof FormProvider<BulkMarketplacePriceFormInput, unknown>>,
    'children'
  >) => {
  const { setActivePosEntity } = useActivePosEntityContext<ListingDetails>();
  const {
    eventsExpansion: { refreshExpandedListItems },
  } = useCatalogDataContext();
  const { setSelectionMode } = useMultiSelectionContext();
  const { setProgress, setPreview, setMainDialogOpened } =
    useBulkEditHubContext();
  const [isLoading, setIsLoading] = useState(false);
  const input = watch();

  const onClose = useCallback(
    async (
      newProgress?: BulkEditProgress,
      closeMainDialogForPopover?: boolean
    ) => {
      if (newProgress?.step === BulkEditStep.Done) {
        setIsRefreshing(true);
        setActivePosEntity(0);

        await refreshExpandedListItems();
        setSelectionMode(undefined);
        if (closeMainDialogForPopover) {
          setMainDialogOpened(false);
        } else {
          setProgress(undefined);
          setPreview(undefined);
        }
        setIsRefreshing(false);
        // progress kept to allow the BulkEditStatusPopover to show the result
      }
      onCancel();
    },
    [
      onCancel,
      refreshExpandedListItems,
      setActivePosEntity,
      setMainDialogOpened,
      setPreview,
      setProgress,
      setSelectionMode,
    ]
  );

  const onBulkEditDone = useCallback(
    async (doneProgress: BulkEditProgress, finalErrors: string[]) => {
      if (finalErrors.length === 0 && doneProgress.step === BulkEditStep.Done) {
        onClose(doneProgress, true);
      }
    },
    [onClose]
  );

  const { bulkEditHub, progress, preview, initJob } = useBulkEditHub(
    ActionOutboxEntityType.Listing,
    BulkActionType.UpdateMarketplacePrices,
    updateKey,
    onBulkEditDone
  );

  useEffect(() => {
    reset(getDefaultValues());
    initJob();
    return () => {
      setMainDialogOpened(false);
      if (progress) {
        setSelectionMode(undefined);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasBackgroundBulkEditFeature =
    bulkEditHub?.state === HubConnectionState.Connected;

  const [isRefreshing, setIsRefreshing] = useState(false);

  const onSubmitHandler = useCallback(
    (onPreviewReceived?: (preview: BulkEditPreviewWithDetails) => void) => {
      setIsLoading(true);
      if (onPreviewReceived) {
        onOkay(null, false, onPreviewReceived);
      } else {
        handleSubmit((i) =>
          onOkay(i, hasBackgroundBulkEditFeature, undefined, preview)
        )();
      }
      setIsLoading(false);
    },
    [onOkay, handleSubmit, hasBackgroundBulkEditFeature, preview]
  );

  const isInputValid = useMemo(() => {
    // POS-9555: without the selection's fee rows we cannot price, so block submit rather
    // than fall back to the account-level fee (which would mis-price international listings)
    if (isOfficesLoading || hasSelectionError) return false;
    return (
      isListingDetailsInputChanged(
        input,
        formState.defaultValues as ListingDetailsUpdateInput
      ) || hasOfficePriceValue(input.officePriceUpdates)
    );
  }, [input, formState.defaultValues, isOfficesLoading, hasSelectionError]);

  const submittButtonRef = useRef<HTMLButtonElement>(null);

  return (
    <Stack direction="column" width="full" height="full">
      <div style={{ flex: 1, minHeight: 0 }}>
        <BulkEditStatus
          entityType={ActionOutboxEntityType.Listing}
          isLoading={isRefreshing || isLoading || isOfficesLoading}
          updateKey={updateKey}
        >
          <MarketplacePricesForm
            event={event}
            officePriceRows={officePriceRows}
            hasSelectionError={hasSelectionError}
          />
        </BulkEditStatus>
      </div>

      <Stack justifyContent="spaceBetween" width="full">
        <BulkEditFooter
          entityType={ActionOutboxEntityType.Listing}
          isLoading={isLoading}
          hasBackgroundBulkEditFeature={hasBackgroundBulkEditFeature}
          onClose={onClose}
          onSubmit={onSubmitHandler}
          disabled={!isInputValid}
          submittButtonRef={submittButtonRef}
        />
      </Stack>
    </Stack>
  );
};

const MarketplacePricesForm = ({
  event,
  officePriceRows,
  hasSelectionError,
}: {
  event: Event | undefined;
  officePriceRows: OfficePriceRow[];
  hasSelectionError: boolean;
}) => {
  const { loginContext } = useAppContext();
  const { getUiCurrency } = useLocalizationContext();
  const uiCurrency = useMemo(
    () =>
      getUiCurrency(loginContext?.user?.activeAccount?.currencyCode ?? 'USD'),
    [getUiCurrency, loginContext?.user?.activeAccount?.currencyCode]
  );
  const marketplaceAccountSetting = useMemo(() => {
    return (
      loginContext?.user?.activeAccount?.marketplaceSettings ?? []
    ).reduce(
      (res, mp) => {
        res[mp.mkp] = mp;
        return res;
      },
      {} as Record<Marketplace, MarketplaceAccountSetting>
    );
  }, [loginContext?.user?.activeAccount?.marketplaceSettings]);

  const canChangePricer = useUserHasAnyOfPermissions(
    Permission.Inventory_SetPrice
  );

  const { watch, setValue } = useFormContext<BulkMarketplacePriceFormInput>();
  const listPrice = watch('listPrice');
  const marketplacePriceUpdates = watch('marketplacePriceUpdates');

  const isMultiOffice = getOfficePriceMode(officePriceRows) === 'multi';
  const isPriceOverrideDisabled =
    isMarketplacePriceOverrideDisabled(officePriceRows);
  const officePriceUpdates = watch('officePriceUpdates');
  const stubHubSellerFee = getStubHubSellerFee(
    officePriceRows,
    marketplaceAccountSetting[Marketplace.StubHub]?.sellerFee
  );

  const logos = createSquareMarketplaceLogos(false);

  const { onPriceChanged, warningDialog, alerts } = usePriceChangeDialogs(
    event?.virtId,
    undefined,
    true
  );

  const onPriceEdit = useCallback(
    (newPrice: number, priceByMarketplaceEnabled: boolean, idx: number) => {
      // Multi-office: no single StubHub price to derive markups from
      if (isMultiOffice) return;
      if (listPrice) {
        const newMarkup = (newPrice - listPrice) / listPrice;

        if (priceByMarketplaceEnabled) {
          setValue(
            `marketplacePriceUpdates.${idx}.markup`,
            roundToPrecision(newMarkup * 100, 2)
          );
          onPriceChanged();
        }
      }
    },
    [isMultiOffice, listPrice, onPriceChanged, setValue]
  );

  return (
    <Stack
      direction="column"
      gap="xxl"
      style={{ padding: vars.spacing.xs, color: vars.color.textPrimaryV2 }}
    >
      {(listPrice != null ||
        hasOfficePriceValue(officePriceUpdates) ||
        marketplacePriceUpdates.some((m) => m.listPrice != null)) && (
        <DetailGroup style={{ gridColumn: '1 / span 3' }}>
          <WarningMessage
            message={
              <Content
                id={ContentId.BulkEditListPriceAdjustingFloorCeilingWarning}
              />
            }
          />
        </DetailGroup>
      )}
      {hasSelectionError ? (
        <WarningMessage
          message={<Content id={ContentId.SomethingWentWrongTryAgain} />}
        />
      ) : isMultiOffice ? (
        <OfficeStubHubManualPrices
          officePriceRows={officePriceRows}
          disabled={!canChangePricer}
          currency={uiCurrency}
          MarketplaceLog={logos[Marketplace.StubHub]}
          onPriceChanged={onPriceChanged}
        />
      ) : (
        <StubHubManualPrice
          disabled={!canChangePricer}
          currency={uiCurrency}
          MarketplaceLog={logos[Marketplace.StubHub]}
          sellerFee={stubHubSellerFee}
          onPriceChanged={onPriceChanged}
        />
      )}
      <Stack width="full" direction="column" gap="l">
        <MarketplacePriceInputHeader />
        <Stack width="full" direction="column" gap="l">
          {marketplacePriceUpdates.map((mp, index) => {
            const marketplaceLogo = logos[mp.marketplace];
            return (
              <MarketplacePriceInputTile
                key={mp.marketplace}
                inputIndex={index}
                currency={uiCurrency}
                MarketplaceLog={marketplaceLogo}
                marketplaceAccountSettings={
                  marketplaceAccountSetting[mp.marketplace]
                }
                disabled={!canChangePricer}
                isPriceOverrideDisabled={isPriceOverrideDisabled}
                onPriceEdit={onPriceEdit}
              />
            );
          })}
        </Stack>
      </Stack>
      <AlertWithSuppressionDialog
        headerText={<Content id={ContentId.ManualPriceWarning} />}
        size="md"
        {...warningDialog.dialogProps}
        alerts={alerts}
        viagogoVirtualId={event?.virtId}
        onOkay={() => warningDialog.closeDialog()}
        onCancel={() => warningDialog.closeDialog()}
      />
    </Stack>
  );
};
